// client/src/components/Navbar.jsx
// 🎯 STEP 1: ADD THIS MISSING IMPORT (Vital for line 8)
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { recordLearningPathExplored } from "@/lib/journeyMilestones";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, auth, logout, loading, isAuthenticated } = useAuth();

  const [showStreakModal, setShowStreakModal] = useState(false);

  const funnelHiddenPaths = [
    "/level-check",
    "/smart-signup",
    "/activation",
    "/start-practice",
    "/webinar",
  ];

  const shouldHideNavbar = funnelHiddenPaths.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(path + "/"),
  );

  if (shouldHideNavbar) return null;

  const storedTrack =
    typeof window !== "undefined" ? localStorage.getItem("fj_track") || "" : "";

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";

  const resolvedTrack = pathname.startsWith("/i/")
    ? "intermediate"
    : pathname.startsWith("/b/")
      ? "beginner"
      : String(storedTrack || user?.track || "").toLowerCase() ===
          "intermediate"
        ? "intermediate"
        : "beginner";

  const trackPath =
    resolvedTrack === "intermediate" ? "/i/lessons" : "/b/lessons";

  // ✅ LOADING GUARD — THIS IS THE KEY
  if (loading) {
    return (
      <nav className="flex items-center px-6 py-4 shadow-sm bg-white">
        <span className="text-[22px] md:text-[24px] leading-none tracking-tight">
          <span className="text-purple-700 font-extrabold">FluencyJet</span>
          <span className="text-purple-700/90 font-medium ml-2">
            Sentence Master
          </span>
        </span>
      </nav>
    );
  }

  const isAdmin = user?.role === "ADMIN";

  function handleLoginClick() {
    navigate("/login");
  }

  function handleLogoutClick() {
    logout();
    navigate("/", { replace: true });
  }

  async function handleLessonsClick(event) {
    event.preventDefault();
    await recordLearningPathExplored();
    navigate(lessonsHref);
  }

  const storedUser = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("user") || "null") ||
        JSON.parse(localStorage.getItem("fj_user") || "null")
      );
    } catch {
      return null;
    }
  })();

  const effectivePlan = String(
    user?.plan || auth?.user?.plan || auth?.plan || storedUser?.plan || "FREE",
  ).toUpperCase();

  const effectiveTrack = String(
    user?.track ||
      auth?.user?.track ||
      auth?.track ||
      storedUser?.track ||
      "BEGINNER",
  ).toUpperCase();

  const lastTrack = String(
    localStorage.getItem("fj_last_track") || effectiveTrack || "BEGINNER",
  ).toUpperCase();

  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "";

  const currentSearch =
    typeof window !== "undefined" ? window.location.search : "";

  const currentDifficulty = new URLSearchParams(currentSearch).get(
    "difficulty",
  );

  const currentRouteTrack =
    currentPath.startsWith("/i/") || currentDifficulty === "intermediate"
      ? "INTERMEDIATE"
      : currentPath.startsWith("/b/") || currentDifficulty === "beginner"
        ? "BEGINNER"
        : lastTrack || effectiveTrack || "BEGINNER";

  const lessonsHref =
    currentRouteTrack === "INTERMEDIATE" ? "/i/lessons" : "/b/lessons";

  const isPaidUser =
    user?.has_access === true ||
    user?.hasAccess === true ||
    auth?.has_access === true ||
    auth?.hasAccess === true ||
    auth?.user?.has_access === true ||
    auth?.user?.hasAccess === true ||
    storedUser?.has_access === true ||
    storedUser?.hasAccess === true ||
    effectivePlan === "PRO" ||
    effectivePlan === "LIFETIME" ||
    String(user?.tier_level || "").toLowerCase() === "pro" ||
    String(auth?.user?.tier_level || "").toLowerCase() === "pro" ||
    String(storedUser?.tier_level || "").toLowerCase() === "pro";

  return (
    <nav className="w-full bg-white shadow-sm overflow-x-hidden">
      <div className="mx-auto max-w-6xl px-4 py-3 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          {/* Logo */}
          <Link
            to="/"
            className="text-xl sm:text-2xl font-bold text-purple-700 leading-tight whitespace-nowrap"
          >
            FluencyJet <span className="font-normal">Sentence Master</span>
          </Link>

          {/* 🔥 Clickable Streak Flame Trigger */}
          {user?.daily_streak > 0 && (
            <button
              onClick={() => setShowStreakModal(true)}
              className="flex items-center gap-1 bg-orange-50 border border-orange-100 px-3 py-1 rounded-full hover:scale-105 transition-transform"
            >
              <span>🔥 {user.daily_streak}</span>
              {/* 🛡️ Streak Freeze Shield for Premium Users */}
              {(user?.plan === "BEGINNER" || auth?.has_access) && (
                <span className="text-[10px] ml-1" title="Protected">
                  🛡️
                </span>
              )}
            </button>
          )}
          {/* Navigation */}
          {/* 🎯 THE FLEXIBLE NAV: No scrollbars, no cut-offs */}
          <div className="flex items-center gap-2 sm:gap-4 sm:ml-auto min-w-0">
            {!isAuthenticated && (
              <>
                <button
                  onClick={handleLoginClick}
                  className="text-sm sm:text-base font-semibold text-slate-900 hover:text-purple-700"
                >
                  Login
                </button>

                <Link
                  to="/level-check"
                  className="shrink-0 rounded-full bg-yellow-400 px-5 py-3 text-sm sm:text-base font-extrabold text-slate-950 shadow-lg shadow-yellow-300/30 transition hover:-translate-y-0.5 hover:bg-yellow-500"
                >
                  Check My Level
                </Link>
              </>
            )}

            <div className="hidden md:flex items-center flex-wrap justify-end gap-4 sm:gap-6 min-w-0">
              {isAuthenticated && (
                <>
                  <Link className="text-sm sm:text-base" to="/dashboard">
                    Dashboard
                  </Link>
                  {/* 🎯 THE SMART UNIT LABEL */}
                  <Link
                    className="text-sm sm:text-base flex items-center gap-1"
                    to={lessonsHref}
                    onClick={handleLessonsClick}
                  >
                    Lessons
                    {user?.current_unit && (
                      <span className="text-[10px] bg-indigo-100 text-indigo-600 px-2 py-0.5 rounded-full font-black">
                        U{user.current_unit}
                      </span>
                    )}
                  </Link>

                  <Link className="text-sm sm:text-base" to="/leaderboard">
                    Leaderboard
                  </Link>

                  {/* 🎯 Hide Full Access CTA if user has paid access */}
                  {!isPaidUser && (
                    <Link
                      to="/spoken-english-offer"
                      className="shrink-0 px-4 py-2 bg-yellow-400 text-black rounded-full font-semibold text-sm sm:text-base"
                    >
                      Upgrade
                    </Link>
                  )}
                  {isAdmin && (
                    <Link className="text-sm sm:text-base" to="/admin">
                      Admin
                    </Link>
                  )}
                  <button
                    onClick={handleLogoutClick}
                    className="shrink-0 px-4 py-2 bg-gray-800 text-white rounded-full shadow text-sm sm:text-base"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
          {/* 🎯 Premium Streak Flame */}
          {showStreakModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
              <div className="bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl relative animate-in fade-in zoom-in duration-300">
                <button
                  onClick={() => setShowStreakModal(false)}
                  className="absolute top-6 right-6 text-slate-400"
                >
                  ✕
                </button>
                <div className="text-center">
                  <div className="text-6xl mb-4">🔥</div>
                  <h3 className="text-2xl font-black text-slate-900">
                    {user?.daily_streak} Day Streak!
                  </h3>
                  <p className="text-slate-500 mt-2 text-sm font-medium">
                    Keep practicing daily to build your fluency engine.
                  </p>
                  <div className="mt-6 p-4 bg-orange-50 rounded-2xl border border-orange-100 flex justify-around">
                    {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                      <div
                        key={i}
                        className={`h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-bold ${i < user?.daily_streak % 7 ? "bg-orange-500 text-white shadow-sm" : "bg-white text-slate-300"}`}
                      >
                        {d}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
